import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Combobox, Transition } from '@headlessui/react';
import { MagnifyingGlassIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import {
  searchCollaboratorsByName,
  type CollaboratorSearchResult,
  type HighlightRange,
} from '@/services/collaboratorSearchService';
import { useDebounce } from '@/hooks/useDebounce';
import CollaboratorAvatar from '@/features/Collaborators/Shared/CollaboratorAvatar/CollaboratorAvatar';

const MIN_QUERY_LENGTH = 3;
const DEFAULT_LIMIT = 8;

// Helper to render highlighted text
function HighlightedText({ text, ranges }: { text: string; ranges?: HighlightRange[] }) {
  if (!ranges || ranges.length === 0) {
    return <span className="text-sm font-semibold text-raro-gray-900">{text}</span>;
  }

  const sortedRanges = [...ranges]
    .filter(r => r.start >= 0 && r.end > r.start && r.end <= text.length)
    .sort((a, b) => a.start - b.start);

  const parts: React.ReactNode[] = [];
  let lastIndex = 0;

  sortedRanges.forEach((range, i) => {
    if (range.start > lastIndex) {
      parts.push(
        <span key={`text-${i}`} className="text-sm font-semibold text-raro-gray-900">
          {text.slice(lastIndex, range.start)}
        </span>
      );
    }
    parts.push(
      <mark key={`mark-${i}`} className="rounded bg-primary-100 px-0.5 text-sm font-semibold text-primary-800">
        {text.slice(range.start, range.end)}
      </mark>
    );
    lastIndex = range.end;
  });

  if (lastIndex < text.length) {
    parts.push(
      <span key="text-end" className="text-sm font-semibold text-raro-gray-900">
        {text.slice(lastIndex)}
      </span>
    );
  }

  return <span className="text-sm font-semibold leading-tight">{parts}</span>;
}

export function GlobalCollaboratorSearch() {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState<CollaboratorSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const debouncedQuery = useDebounce(query, 300);
  const requestIdRef = useRef(0);

  // Effect to handle search
  useEffect(() => {
    const normalizedQuery = debouncedQuery.trim();

    if (normalizedQuery.length < MIN_QUERY_LENGTH) {
      setOptions([]);
      setHasSearched(false);
      setLoading(false);
      return;
    }

    const currentRequestId = ++requestIdRef.current;
    setLoading(true);

    searchCollaboratorsByName(normalizedQuery, DEFAULT_LIMIT)
      .then((results) => {
        if (currentRequestId === requestIdRef.current) {
          setOptions(results);
          setHasSearched(true);
        }
      })
      .catch((error) => {
        console.error('Search error:', error);
        if (currentRequestId === requestIdRef.current) {
          setOptions([]);
        }
      })
      .finally(() => {
        if (currentRequestId === requestIdRef.current) {
          setLoading(false);
        }
      });
  }, [debouncedQuery]);

  const handleSelect = (item: CollaboratorSearchResult | null) => {
    if (item) {
      navigate(item.profilePath || `/collaborators/${item.collaboratorId}`);
      setQuery('');
      setOptions([]);
      setHasSearched(false);
    }
  };

  const getDisplayStatus = (status: string | null) => {
    if (!status) return undefined;
    const s = status.toUpperCase();
    if (s === 'ACTIVE') return 'ativo';
    if (s === 'INACTIVE') return 'inativo';
    return status;
  };

  return (
    <div className="w-full max-w-xl">
      <Combobox value={null} onChange={handleSelect} nullable>
        <div className="relative">
          <MagnifyingGlassIcon
            className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-primary-500"
            aria-hidden="true"
          />
          <Combobox.Input
            className="w-full rounded-2xl border border-primary-100 bg-white/90 py-2.5 pl-10 pr-10 text-sm font-medium text-raro-gray-900 shadow-inner focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200 placeholder:text-raro-gray-400"
            placeholder="Buscar colaborador pelo nome"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            autoComplete="off"
          />
          <div className="absolute inset-y-0 right-3 flex items-center">
            {loading && (
              <ArrowPathIcon className="h-4 w-4 animate-spin text-primary-500" aria-hidden="true" />
            )}
          </div>
        </div>

        <Transition
          as={React.Fragment}
          show={!loading && (options.length > 0 || hasSearched) && query.length >= MIN_QUERY_LENGTH}
          enter="transition ease-out duration-200"
          enterFrom="opacity-0 -translate-y-2"
          enterTo="opacity-100 translate-y-0"
          leave="transition ease-in duration-150"
          leaveFrom="opacity-100 translate-y-0"
          leaveTo="opacity-0 -translate-y-2"
        >
          <Combobox.Options static className="absolute mt-2 max-h-80 w-full overflow-auto rounded-2xl bg-white py-2 shadow-2xl ring-1 ring-primary-100 focus:outline-none z-50">
            {options.length === 0 && hasSearched ? (
              <div className="px-4 py-3 text-sm text-raro-gray-600 text-center">
                Nenhum colaborador encontrado
              </div>
            ) : (
              options.map((item) => (
                <Combobox.Option
                  key={item.collaboratorId}
                  value={item}
                  className={({ active }) =>
                    clsx(
                      'cursor-pointer px-4 py-3 transition border-b border-gray-50 last:border-0',
                      active ? 'bg-primary-50' : 'bg-white'
                    )
                  }
                >
                  <div className="flex items-center gap-3">
                    <CollaboratorAvatar
                      name={item.name}
                      photoUrl={item.avatarUrl}
                      size="medium"
                      activityStatus={getDisplayStatus(item.status)}
                      isRaroAcademyAlumnus={item.isRaroAcademyAlumnus || false}
                    />
                    <div className="flex flex-1 flex-col">
                      <HighlightedText text={item.name} ranges={item.highlights?.name} />
                      {item.positionName && (
                        <span className="text-xs text-raro-gray-500 mt-0.5">
                          {item.positionName}
                        </span>
                      )}
                    </div>
                  </div>
                </Combobox.Option>
              ))
            )}
          </Combobox.Options>
        </Transition>
      </Combobox>
    </div>
  );
}
